// ─── components/WelcomeBackDigest.jsx ──────────────────────────────────────
// "While you were gone": when you come back to the site after leaving it
// (closing or reloading the tab, or logging out), a modal lists everything
// that happened in the meantime: proposals, offers, decisions, payments,
// reviews and messages. Plus the "Needs you" queue (GET
// /api/dashboard/action-queue, the same list the floating tracker reads), so
// anything waiting on you is one click away.
//
// The moment you left is kept in localStorage per account. It's written on
// `pagehide` and when this component unmounts, which is what logging out
// does (App renders it only while signed in). Nothing new since then means
// no modal, so a plain reload never shows one.
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { PiBriefcase, PiChatText, PiCreditCard, PiFileText, PiStar, PiBell, PiX } from 'react-icons/pi';
import useFocusTrap from '../../hooks/useFocusTrap';

const QUEUE_ICON = { order: PiFileText, contract: PiFileText, message: PiChatText, invoice: PiCreditCard };
const CATEGORY_ICON = { messages: PiChatText, contracts: PiBriefcase, payouts: PiCreditCard, reviews: PiStar, grades: PiFileText };
const MAX_SHOWN = 6;

const leftKey = (userId) => `uf:lastLeft:${userId}`;
function readLeft(userId) {
  try { return Number(localStorage.getItem(leftKey(userId))) || 0; } catch { return 0; }
}
function markLeft(userId) {
  try { localStorage.setItem(leftKey(userId), String(Date.now())); } catch { /* storage blocked */ }
}

function timeLabel(date) {
  return new Date(date).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function WelcomeBackDigest({ user }) {
  const userId = user?._id;
  // Read once per arrival. State survives StrictMode's dev-only effect
  // replay, so the cleanup's markLeft below can't overwrite it first.
  const [since] = useState(() => (userId ? readLeft(userId) : 0));
  const [queueItems, setQueueItems] = useState([]);
  const [happened, setHappened] = useState([]);
  const [moreCount, setMoreCount] = useState(0);
  const [visible, setVisible] = useState(false);

  // Remember when they leave.
  useEffect(() => {
    if (!userId) return undefined;
    const onHide = () => markLeft(userId);
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      markLeft(userId);
    };
  }, [userId]);

  // On arrival, show what happened since then.
  useEffect(() => {
    if (!userId || !since) return undefined; // first visit we've tracked: nothing to compare against
    let cancelled = false;
    (async () => {
      const get = (url, pick) => fetch(url, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => (d ? pick(d) : []))
        .catch(() => []);
      const [queue, notifications] = await Promise.all([
        get('/api/dashboard/action-queue', (d) => d.items || []),
        get('/api/notifications?limit=50', (d) => d.notifications || []),
      ]);
      if (cancelled) return;
      const fresh = notifications.filter((n) => !n.read && new Date(n.createdAt).getTime() > since);
      if (fresh.length === 0) return;
      setHappened(fresh.slice(0, MAX_SHOWN));
      setMoreCount(Math.max(0, fresh.length - MAX_SHOWN));
      setQueueItems(queue.slice(0, 4));
      setVisible(true);
    })();
    return () => { cancelled = true; };
  }, [userId, since]);

  const dismiss = () => setVisible(false);
  const panelRef = useRef(null);
  useFocusTrap(panelRef, visible, dismiss);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-press-ink/55 p-5 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="While you were gone"
      onClick={dismiss}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative max-h-[85vh] w-full max-w-[460px] overflow-y-auto rounded-[20px] border border-press-border bg-press-card p-6 font-academy shadow-xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-press-muted transition hover:bg-press-paper-2 hover:text-press-ink"
        >
          <PiX size={16} aria-hidden="true" />
        </button>

        <p className="text-[12px] font-bold text-press-muted">Welcome back</p>
        <p className="mt-1 font-press-display text-xl font-normal text-press-ink">
          {happened.length + moreCount === 1 ? 'One thing happened while you were gone' : `${happened.length + moreCount} things happened while you were gone`}
        </p>
        <p className="mt-0.5 text-xs text-press-muted">Since you left on {timeLabel(since)}</p>

        <ul className="mt-4 flex list-none flex-col gap-1.5 p-0">
          {happened.map((n) => {
            const Icon = CATEGORY_ICON[n.category] || PiBell;
            return (
              <li key={n._id}>
                <Link
                  to={n.link || '/notifications'}
                  onClick={dismiss}
                  className="flex items-start gap-2.5 rounded-xl px-2 py-2 transition hover:bg-press-paper-2"
                >
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-press-paper-2 text-press-ink">
                    <Icon size={14} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold text-press-ink">{n.title}</span>
                    {n.body && <span className="line-clamp-2 block text-[12px] text-press-muted">{n.body}</span>}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        {moreCount > 0 && (
          <Link to="/notifications" onClick={dismiss} className="mt-1 inline-block px-2 text-[13px] font-semibold text-press-ink underline underline-offset-[3px]">
            And {moreCount} more
          </Link>
        )}

        {queueItems.length > 0 && (
          <div className="mt-5 border-t border-press-border pt-4">
            <p className="mb-2 text-[12px] font-bold text-press-muted">Waiting on you</p>
            <div className="flex flex-col gap-1.5">
              {queueItems.map((item, i) => {
                const Icon = QUEUE_ICON[item.type] || PiFileText;
                return (
                  <Link
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    to={item.link}
                    onClick={dismiss}
                    className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 transition hover:bg-press-paper-2"
                  >
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-press-paper-2 text-press-ink">
                      <Icon size={14} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-press-ink">{item.title}</span>
                      <span className="block truncate text-[12px] text-press-muted">{item.subtitle}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={dismiss}
          className="mt-5 w-full rounded-full bg-press-ink px-5 py-2.5 text-sm font-semibold text-press-paper transition hover:bg-press-ink/90"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

WelcomeBackDigest.propTypes = { user: PropTypes.object };
